import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { VideoPanel } from "components/loadingWindowEditor/VideoPanel";
import { TitledTabs, TabSpec } from "components/common/titledTabs";
import { sorted } from "helpers";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import {
  useCallback,
  useDeferredValue,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HeatQualityIssue,
  LoadingEditRead,
  ProposeLoadingEditRequest,
  useGetHeatQualityIssuesQuery,
  useGetLoadingWindowChargeEventsQuery,
  useGetLoadingWindowDropsQuery,
  useGetLoadingWindowsQuery,
  useListConsumptionAreasQuery,
  useListLoadingEditsQuery,
  useSubmitAndApplyLoadingEditsMutation,
  useSubmitLoadingReviewsMutation,
  useReviseAndApplyLoadingEditsMutation,
} from "src/store/api/generatedApi";
import {
  computeClaims,
  KeyedWindow,
  paintWindow,
  PlannedWindow,
  WindowClaim,
} from "./loadingEditPlanning";
import {
  EditDialogWindow,
  LoadingWindowEditDialog,
} from "./LoadingWindowEditDialog";
import { LoadingChargeEventsTable } from "./LoadingChargeEventsTable";
import { LoadingEditLedgerTable } from "./LoadingEditLedgerTable";
import { LoadingHeatIssuesTable } from "./LoadingHeatIssuesTable";
import { LoadingReviewPane, type ReviewMark } from "./LoadingReviewPane";
import { DEFAULT_TRACE_BG_ALPHA, MassTraceOverlay } from "./MassTraceOverlay";
import {
  type DropCategory,
  heatColorFor,
  LoadingWindowTimeline,
  type TimelineChargeEvent,
  type TimelineDrop,
  type TimelineWindow,
} from "./LoadingWindowTimeline";

type LedgerTabKey = "edits" | "heatIssues" | "chargeEvents";

const DROP_LEGEND = [
  { color: "success.main", key: "bucketDrop" },
  { color: "error.main", key: "bucketDropRemoval" },
  { color: "grey.600", key: "bucketDropRearrangement" },
  { color: "warning.main", key: "unassignedDrop" },
] as const;

const DROP_CATEGORY_KEY: Record<DropCategory, string> = {
  addition: "bucketDrop",
  removal: "bucketDropRemoval",
  rearrangement: "bucketDropRearrangement",
  unassigned: "unassignedDrop",
};

dayjs.extend(utc);

const SPAN_FORMAT = "HH:mm:ss";
const NEW_WINDOW_MS = 5 * 60 * 1000;
// Pad the day either side so windows straddling midnight aren't clipped.
const PAD_HOURS = 6;

const valueKey = (window: PlannedWindow): string =>
  `${window.bucket}:${window.startMs}:${window.endMs}:${window.heatId}:${window.basketNumber}`;

const claimToRequest = (claim: WindowClaim): ProposeLoadingEditRequest => ({
  area_name: claim.bucket,
  start_time: dayjs.utc(claim.startMs).toISOString(),
  end_time: dayjs.utc(claim.endMs).toISOString(),
  target_status: claim.targetStatus,
  target_heat_id: claim.heatId,
  target_basket_number: claim.basketNumber,
  rationale: null,
});

export const LoadingWindowEditorPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();

  const [selectedDay, setSelectedDay] = useState(() =>
    dayjs.utc().subtract(1, "day").startOf("day")
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [domain, setDomain] = useState<{
    startMs: number;
    endMs: number;
  } | null>(null);
  const [desired, setDesired] = useState<KeyedWindow[]>([]);
  const [syncedSignature, setSyncedSignature] = useState<string | null>(null);
  const [dialogWindow, setDialogWindow] = useState<EditDialogWindow | null>(
    null
  );
  const [commitOpen, setCommitOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [selectedTabKey, setSelectedTabKey] =
    useState<LedgerTabKey>("heatIssues");
  const [highlightedToAreas, setHighlightedToAreas] = useState<string[]>([]);
  const [videoRangeMs, setVideoRangeMs] = useState<{
    startMs: number;
    endMs: number;
  } | null>(null);
  const [videoCursorMs, setVideoCursorMs] = useState<number | null>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [traceOpen, setTraceOpen] = useState(false);
  const [traceInitialAreas, setTraceInitialAreas] = useState<string[]>([]);
  // Held here rather than in the strip so both survive closing and reopening.
  const [traceSelectedAreas, setTraceSelectedAreas] =
    useState<ReadonlySet<string> | null>(null);
  const [traceBgAlpha, setTraceBgAlpha] = useState(DEFAULT_TRACE_BG_ALPHA);
  const nextKeyRef = useRef(0);

  const periodStart = useMemo(
    () => selectedDay.subtract(PAD_HOURS, "hour"),
    [selectedDay]
  );
  const periodEnd = useMemo(
    () => selectedDay.add(1, "day").add(PAD_HOURS, "hour"),
    [selectedDay]
  );
  const startIso = periodStart.toISOString();
  const endIso = periodEnd.toISOString();

  const windowsQuery = useGetLoadingWindowsQuery({
    tenantName,
    start: startIso,
    end: endIso,
  });
  const editsQuery = useListLoadingEditsQuery({
    tenantName,
    start: startIso,
    end: endIso,
  });
  const heatIssuesQuery = useGetHeatQualityIssuesQuery({
    tenantName,
    start: startIso,
    end: endIso,
  });
  const dropsQuery = useGetLoadingWindowDropsQuery({
    tenantName,
    start: startIso,
    end: endIso,
  });
  const chargeEventsQuery = useGetLoadingWindowChargeEventsQuery({
    tenantName,
    start: startIso,
    end: endIso,
  });
  const areasQuery = useListConsumptionAreasQuery({ tenantName });
  const [submitAndApply, submitState] = useSubmitAndApplyLoadingEditsMutation();
  const [submitReviews, reviewState] = useSubmitLoadingReviewsMutation();
  const [reviseAndApply] = useReviseAndApplyLoadingEditsMutation();

  const originalKeyed = useMemo(
    (): KeyedWindow[] =>
      (windowsQuery.data?.windows ?? []).map((window) => ({
        key: `${window.area_name}:${window.start_time}:${window.basket_number}`,
        bucket: window.area_name,
        startMs: dayjs.utc(window.start_time).valueOf(),
        endMs: dayjs.utc(window.end_time).valueOf(),
        heatId: window.heat_id,
        basketNumber: window.basket_number,
      })),
    [windowsQuery.data]
  );

  // Compare compiled windows by content, not reference — a refetch hands back a
  // new array even when the windows are unchanged.
  const originalSignature = useMemo(
    () =>
      `${startIso}|${endIso}\n` +
      originalKeyed
        .map(
          (window) =>
            `${window.key}|${window.startMs}|${window.endMs}|${window.heatId}|${window.basketNumber}`
        )
        .join(";"),
    [originalKeyed, startIso, endIso]
  );

  // Reset the working set only when the compiled windows actually change (day
  // change or post-commit recompile), so local uncommitted edits survive a refetch.
  if (syncedSignature !== originalSignature) {
    setSyncedSignature(originalSignature);
    setDesired(originalKeyed);
  }

  const claims = useMemo(
    () => computeClaims({ original: originalKeyed, desired }),
    [originalKeyed, desired]
  );

  const originalValueKeys = useMemo(
    () => new Set(originalKeyed.map(valueKey)),
    [originalKeyed]
  );

  const timelineWindows = useMemo(
    (): TimelineWindow[] =>
      desired.map((window) => ({
        id: window.key,
        bucket: window.bucket,
        startMs: window.startMs,
        endMs: window.endMs,
        heatId: window.heatId,
        basketNumber: window.basketNumber,
        pending: !originalValueKeys.has(valueKey(window)),
      })),
    [desired, originalValueKeys]
  );

  const PROPOSAL_KEY_PREFIX = "proposal:";
  const pendingProposals = useMemo(
    () =>
      (editsQuery.data?.edits ?? []).filter(
        (edit) => edit.latest_decision === null
      ),
    [editsQuery.data]
  );
  const [reviewMarks, setReviewMarks] = useState<Map<number, ReviewMark>>(
    () => new Map()
  );
  const [amendments, setAmendments] = useState<
    Map<number, { startMs: number; endMs: number }>
  >(() => new Map());
  const proposalWindows = useMemo(
    (): TimelineWindow[] =>
      pendingProposals.map((edit) => {
        const amendment = amendments.get(edit.id);
        return {
          id: `${PROPOSAL_KEY_PREFIX}${edit.id}`,
          bucket: edit.area_name,
          startMs: amendment?.startMs ?? dayjs.utc(edit.start_time).valueOf(),
          endMs: amendment?.endMs ?? dayjs.utc(edit.end_time).valueOf(),
          heatId: edit.target_heat_id ?? "",
          basketNumber: edit.target_basket_number ?? 0,
          proposal: true,
          amended: amendment !== undefined,
          idle: edit.target_status === "idle",
          reviewMark: reviewMarks.get(edit.id),
        };
      }),
    [pendingProposals, amendments, reviewMarks]
  );
  const selectedProposal = useMemo(() => {
    if (!selectedKey?.startsWith(PROPOSAL_KEY_PREFIX)) return null;
    const proposalId = Number(selectedKey.slice(PROPOSAL_KEY_PREFIX.length));
    return pendingProposals.find((edit) => edit.id === proposalId) ?? null;
  }, [selectedKey, pendingProposals]);

  const markReview = (editId: number, mark: ReviewMark) => {
    setAmendments((previous) => {
      if (!previous.has(editId)) return previous;
      const next = new Map(previous);
      next.delete(editId);
      return next;
    });
    setReviewMarks((previous) => {
      const next = new Map(previous);
      if (next.get(editId) === mark) next.delete(editId);
      else next.set(editId, mark);
      return next;
    });
  };
  const markUnreviewed = (mark: ReviewMark) => {
    setReviewMarks((previous) => {
      const next = new Map(previous);
      for (const edit of pendingProposals) {
        if (!next.has(edit.id) && !amendments.has(edit.id)) {
          next.set(edit.id, mark);
        }
      }
      return next;
    });
  };
  const amendProposal = (editId: number, startMs: number, endMs: number) => {
    setReviewMarks((previous) => {
      if (!previous.has(editId)) return previous;
      const next = new Map(previous);
      next.delete(editId);
      return next;
    });
    setAmendments((previous) =>
      new Map(previous).set(editId, { startMs, endMs })
    );
  };
  const focusProposal = (editId: number) => {
    const proposal = pendingProposals.find((edit) => edit.id === editId);
    if (proposal === undefined) return;
    const startMs = dayjs.utc(proposal.start_time).valueOf();
    const endMs = dayjs.utc(proposal.end_time).valueOf();
    const pad = Math.max((endMs - startMs) * 1.5, 5 * 60_000);
    setDomain(clampDomain({ startMs: startMs - pad, endMs: endMs + pad }));
    setSelectedKey(`${PROPOSAL_KEY_PREFIX}${editId}`);
  };
  const submitReview = async () => {
    if (reviewMarks.size === 0 && amendments.size === 0) return;
    if (reviewMarks.size > 0) {
      await submitReviews({
        tenantName,
        submitLoadingReviewsRequest: {
          reviews: [...reviewMarks].map(([edit_id, decision]) => ({
            edit_id,
            decision,
          })),
        },
      });
    }
    // An amend declines the proposal and applies a linked, adjusted revision;
    // the whole batch lands in one request.
    const revisions = [...amendments].flatMap(([editId, geometry]) => {
      const original = pendingProposals.find((edit) => edit.id === editId);
      if (original === undefined) return [];
      return [
        {
          edit_id: editId,
          start_time: dayjs.utc(geometry.startMs).toISOString(),
          end_time: dayjs.utc(geometry.endMs).toISOString(),
          target_status: original.target_status,
          target_heat_id: original.target_heat_id,
          target_basket_number: original.target_basket_number,
          rationale: original.rationale,
        },
      ];
    });
    if (revisions.length > 0) {
      await reviseAndApply({
        tenantName,
        reviseAndApplyLoadingEditsRequest: { revisions },
      });
    }
    setReviewMarks(new Map());
    setAmendments(new Map());
    setSelectedKey(null);
    await Promise.all([editsQuery.refetch(), windowsQuery.refetch()]);
  };

  // Lanes come from the tenant's areas so empty buckets still appear and a lane
  // survives deleting its last window; the working set is unioned in defensively.
  const buckets = useMemo(
    () =>
      sorted(
        [
          ...new Set([
            ...(areasQuery.data?.areas ?? []).map((area) => area.area_name),
            ...desired.map((window) => window.bucket),
          ]),
        ],
        (bucket) => bucket
      ),
    [areasQuery.data, desired]
  );

  const heatOptions = useMemo(
    () =>
      sorted(
        [...new Set(originalKeyed.map((window) => window.heatId))],
        (heatId) => heatId
      ),
    [originalKeyed]
  );

  const timelineDrops = useMemo(
    (): TimelineDrop[] =>
      (dropsQuery.data?.labelled_heat_drops ?? []).flatMap((group) =>
        group.drops.map((drop): TimelineDrop => {
          const mass = drop.net_drop_mass_to_heat;
          const category: DropCategory =
            mass !== null && mass > 0
              ? "addition"
              : mass !== null && mass < 0
                ? "removal"
                : "rearrangement";
          const label = t(DROP_CATEGORY_KEY[category]);
          const material = drop.material ? ` · ${drop.material}` : "";
          const route = [drop.from_area, drop.to_area]
            .filter(Boolean)
            .join(" → ");
          const routePart = route ? ` · ${route}` : "";
          const time = dayjs.utc(drop.drop_timestamp).format("HH:mm:ss");
          return {
            bucket: group.consumption_area,
            ms: dayjs.utc(drop.drop_timestamp).valueOf(),
            category,
            heatId: group.heat_id,
            toArea: drop.to_area,
            title: `${group.heat_id} · ${label}${material}${routePart} · ${time} UTC`,
          };
        })
      ),
    [dropsQuery.data, t]
  );

  // Unassigned drops land on the lane their destination names when it exists;
  // a destination outside the lane set (a physical pad distinct from the
  // logical buckets) falls through to the synthetic Unassigned lane.
  const unassignedLane = t("unassigned");
  const unassignedTimelineDrops = useMemo(
    (): TimelineDrop[] =>
      (dropsQuery.data?.unassigned_drops ?? []).map((drop): TimelineDrop => {
        const heatHint = drop.attributed_heat_id ?? "—";
        const material = drop.material ? ` · ${drop.material}` : "";
        const route = [drop.from_area, drop.to_area]
          .filter(Boolean)
          .join(" → ");
        const routePart = route ? ` · ${route}` : "";
        const time = dayjs.utc(drop.drop_timestamp).format("HH:mm:ss");
        return {
          bucket:
            drop.to_area !== null && buckets.includes(drop.to_area)
              ? drop.to_area
              : unassignedLane,
          ms: dayjs.utc(drop.drop_timestamp).valueOf(),
          category: "unassigned",
          heatId: heatHint,
          toArea: drop.to_area,
          title: `${t("unassignedDrop")} · ${heatHint}${material}${routePart} · ${time} UTC`,
        };
      }),
    [dropsQuery.data, buckets, unassignedLane, t]
  );

  const hasUnassignedLane = useMemo(
    () =>
      unassignedTimelineDrops.some((drop) => drop.bucket === unassignedLane),
    [unassignedTimelineDrops, unassignedLane]
  );
  const timelineBuckets = useMemo(
    () => (hasUnassignedLane ? [...buckets, unassignedLane] : buckets),
    [buckets, hasUnassignedLane, unassignedLane]
  );
  const nonEditableBuckets = useMemo(
    () => (hasUnassignedLane ? new Set([unassignedLane]) : undefined),
    [hasUnassignedLane, unassignedLane]
  );
  const allTimelineDrops = useMemo(
    () => [...timelineDrops, ...unassignedTimelineDrops],
    [timelineDrops, unassignedTimelineDrops]
  );

  const theme = useTheme();
  const colorForHeat = useCallback(
    (heatId: string): string => heatColorFor(theme, heatId),
    [theme]
  );
  const timelineChargeEvents = useMemo(
    (): TimelineChargeEvent[] =>
      (chargeEventsQuery.data?.heats ?? []).flatMap((heat) =>
        heat.charges.map((charge): TimelineChargeEvent => {
          const time = dayjs.utc(charge.charged_at).format("HH:mm:ss");
          return {
            bucket: charge.consumption_area,
            ms: dayjs.utc(charge.charged_at).valueOf(),
            heatId: heat.heat_id,
            title: `${heat.heat_id} · #${charge.charge_sequence} · ${time} UTC`,
          };
        })
      ),
    [chargeEventsQuery.data]
  );

  const availableToAreas = useMemo(
    () =>
      sorted(
        [
          ...new Set(
            allTimelineDrops
              .map((drop) => drop.toArea)
              .filter((area): area is string => area !== null)
          ),
        ],
        (area) => area
      ),
    [allTimelineDrops]
  );

  const selectedWindow = useMemo(
    () => desired.find((window) => window.key === selectedKey) ?? null,
    [desired, selectedKey]
  );

  // Correlate the selected window to the ledger heuristically: same area + time
  // overlap (a window is compiled from potentially several edits).
  const highlightedEditIds = useMemo(() => {
    if (selectedWindow === null) return new Set<number>();
    return new Set(
      (editsQuery.data?.edits ?? [])
        .filter(
          (edit) =>
            edit.area_name === selectedWindow.bucket &&
            dayjs.utc(edit.start_time).valueOf() < selectedWindow.endMs &&
            dayjs.utc(edit.end_time).valueOf() > selectedWindow.startMs
        )
        .map((edit) => edit.id)
    );
  }, [selectedWindow, editsQuery.data]);

  // Grid highlights re-render the two DataGrids; deferring them keeps the
  // selection outline and the details line on the fast commit.
  const deferredHighlightedEditIds = useDeferredValue(highlightedEditIds);
  const deferredHighlightedHeatId = useDeferredValue(
    selectedWindow?.heatId ?? null
  );

  const effectiveDomain = domain ?? {
    startMs: periodStart.valueOf(),
    endMs: periodEnd.valueOf(),
  };

  // Keep the view inside the fetched interval so a pan/zoom can't expose or edit
  // windows outside the loaded data.
  const clampDomain = (next: { startMs: number; endMs: number }) => {
    const min = periodStart.valueOf();
    const max = periodEnd.valueOf();
    const span = Math.min(next.endMs - next.startMs, max - min);
    const startMs = Math.min(Math.max(next.startMs, min), max - span);
    return { startMs, endMs: startMs + span };
  };

  // The video loads a fixed range (the day by default) so panning/zooming the
  // timeline doesn't reload it; "recenter" snaps it to the visible range.
  const videoRange = videoRangeMs ?? {
    startMs: periodStart.valueOf(),
    endMs: periodEnd.valueOf(),
  };
  // Stable Dayjs identities: VideoPanel resets playback when its window props
  // change, and a cursor tick re-renders this component every frame.
  const videoWindowStart = useMemo(
    () => dayjs.utc(videoRange.startMs),
    [videoRange.startMs]
  );
  const videoWindowEnd = useMemo(
    () => dayjs.utc(videoRange.endMs),
    [videoRange.endMs]
  );
  const recenterVideo = () => {
    setVideoRangeMs({ ...effectiveDomain });
  };
  // The trace opens showing the areas that have windows in view, so the first
  // look lands on the lanes being reviewed rather than every car.
  const toggleTrace = () => {
    if (traceOpen) {
      setTraceOpen(false);
      return;
    }
    setTraceInitialAreas([
      ...new Set(
        desired
          .filter(
            (window) =>
              window.startMs < effectiveDomain.endMs &&
              window.endMs > effectiveDomain.startMs
          )
          .map((window) => window.bucket)
      ),
    ]);
    setTraceOpen(true);
  };
  const handleVideoTime = useCallback((timestamp: Date) => {
    setVideoCursorMs(timestamp.getTime());
  }, []);

  const goToDay = (day: Dayjs) => {
    setSelectedDay(day);
    setDomain(null);
    setDialogWindow(null);
    setSelectedKey(null);
    setHighlightedToAreas([]);
    setVideoRangeMs(null);
    setVideoCursorMs(null);
  };

  const zoomBy = (factor: number) => {
    const nextSpan = (effectiveDomain.endMs - effectiveDomain.startMs) * factor;
    if (nextSpan < 60_000) return;
    const center = (effectiveDomain.startMs + effectiveDomain.endMs) / 2;
    setDomain(
      clampDomain({
        startMs: center - nextSpan / 2,
        endMs: center + nextSpan / 2,
      })
    );
  };

  const panBy = (fraction: number) => {
    const shift = (effectiveDomain.endMs - effectiveDomain.startMs) * fraction;
    setDomain(
      clampDomain({
        startMs: effectiveDomain.startMs + shift,
        endMs: effectiveDomain.endMs + shift,
      })
    );
  };

  // Jump the timeline to an edit picked from the ledger: frame its span and
  // select the compiled window covering it (an edit may not map 1:1 to a window).
  const navigateToEdit = useCallback(
    (edit: LoadingEditRead) => {
      const startMs = dayjs.utc(edit.start_time).valueOf();
      const endMs = dayjs.utc(edit.end_time).valueOf();
      const pad = Math.max((endMs - startMs) * 0.5, 60_000);
      setDomain({ startMs: startMs - pad, endMs: endMs + pad });
      const midMs = (startMs + endMs) / 2;
      const window = desired.find(
        (candidate) =>
          candidate.bucket === edit.area_name &&
          candidate.startMs <= midMs &&
          candidate.endMs >= midMs
      );
      setSelectedKey(window?.key ?? null);
    },
    [desired]
  );

  // Jump the timeline to a heat issue picked from the list. An issue carries no
  // area/time span, so frame all compiled windows for that heat; if the heat has
  // none on screen, centre on the issue's loading-start (or tap) time.
  const navigateToHeatIssue = useCallback(
    (issue: HeatQualityIssue) => {
      const heatWindows = desired.filter(
        (window) => window.heatId === issue.heat_id
      );
      if (heatWindows.length > 0) {
        const startMs = Math.min(
          ...heatWindows.map((window) => window.startMs)
        );
        const endMs = Math.max(...heatWindows.map((window) => window.endMs));
        const pad = Math.max((endMs - startMs) * 0.25, 60_000);
        setDomain({ startMs: startMs - pad, endMs: endMs + pad });
        const earliest = heatWindows.reduce((a, b) =>
          a.startMs <= b.startMs ? a : b
        );
        setSelectedKey(earliest.key);
        return;
      }
      const anchor = issue.loading_start_time ?? issue.tap_timestamp;
      if (anchor !== null) {
        const anchorMs = dayjs.utc(anchor).valueOf();
        const halfSpanMs = 30 * 60 * 1000;
        setDomain({
          startMs: anchorMs - halfSpanMs,
          endMs: anchorMs + halfSpanMs,
        });
        setSelectedKey(null);
      }
    },
    [desired]
  );

  const navigateToHeat = useCallback(
    (heatId: string) => {
      const heatWindows = desired.filter((window) => window.heatId === heatId);
      if (heatWindows.length === 0) return;
      const startMs = Math.min(...heatWindows.map((window) => window.startMs));
      const endMs = Math.max(...heatWindows.map((window) => window.endMs));
      const pad = Math.max((endMs - startMs) * 0.25, 60_000);
      setDomain({ startMs: startMs - pad, endMs: endMs + pad });
      const earliest = heatWindows.reduce((a, b) =>
        a.startMs <= b.startMs ? a : b
      );
      setSelectedKey(earliest.key);
    },
    [desired]
  );

  const openWindow = (id: string | null) => {
    if (id === null) return;
    const window = desired.find((candidate) => candidate.key === id);
    if (window === undefined) return;
    setDialogWindow({ ...window, isNew: false });
  };

  const openNewWindow = () => {
    const midMs = (effectiveDomain.startMs + effectiveDomain.endMs) / 2;
    setDialogWindow({
      key: `new:${nextKeyRef.current++}`,
      bucket: buckets[0] ?? "",
      startMs: midMs,
      endMs: midMs + NEW_WINDOW_MS,
      heatId: "",
      basketNumber: null,
      isNew: true,
    });
  };

  const saveWindow = (window: EditDialogWindow) => {
    setDesired(
      paintWindow(desired, {
        key: window.key,
        bucket: window.bucket,
        startMs: window.startMs,
        endMs: window.endMs,
        heatId: window.heatId,
        basketNumber: window.basketNumber ?? 1,
      })
    );
    setDialogWindow(null);
  };

  const deleteWindow = (key: string) => {
    setDesired(desired.filter((window) => window.key !== key));
    setDialogWindow(null);
    setSelectedKey(null);
  };

  const resizeWindow = (id: string, startMs: number, endMs: number) => {
    if (id.startsWith(PROPOSAL_KEY_PREFIX)) {
      amendProposal(
        Number(id.slice(PROPOSAL_KEY_PREFIX.length)),
        startMs,
        endMs
      );
      return;
    }
    const window = desired.find((candidate) => candidate.key === id);
    if (window === undefined) return;
    setDesired(paintWindow(desired, { ...window, startMs, endMs }));
  };

  const createWindow = (bucket: string, startMs: number, endMs: number) => {
    setDialogWindow({
      key: `new:${nextKeyRef.current++}`,
      bucket,
      startMs,
      endMs,
      heatId: "",
      basketNumber: null,
      isNew: true,
    });
  };

  const commit = async () => {
    try {
      await submitAndApply({
        tenantName,
        submitLoadingEditsRequest: { edits: claims.map(claimToRequest) },
      }).unwrap();
      setCommitOpen(false);
    } catch {
      // Left open on failure; surfaced via submitState.isError in the dialog.
    }
  };

  const loadingCount = claims.filter(
    (claim) => claim.targetStatus === "loading"
  ).length;
  const idleCount = claims.length - loadingCount;

  const ledgerTabSpecs: TabSpec<LedgerTabKey>[] = [
    {
      key: "heatIssues",
      title: t("heatIssues"),
      content: (
        <LoadingHeatIssuesTable
          issues={heatIssuesQuery.data?.issues ?? []}
          onIssueClick={navigateToHeatIssue}
          highlightedHeatId={deferredHighlightedHeatId}
        />
      ),
    },
    {
      key: "chargeEvents",
      title: t("chargeEvents"),
      content: (
        <LoadingChargeEventsTable
          heats={chargeEventsQuery.data?.heats ?? []}
          onHeatClick={navigateToHeat}
          colorForHeat={colorForHeat}
        />
      ),
    },
    {
      key: "edits",
      title: t("editLedger"),
      content: (
        <LoadingEditLedgerTable
          edits={editsQuery.data?.edits ?? []}
          highlightedEditIds={deferredHighlightedEditIds}
          onEditClick={navigateToEdit}
        />
      ),
    },
  ];

  return (
    <Stack spacing={2} sx={{ p: 2, height: "100%", minHeight: 0 }}>
      <Typography variant="h1">{t("loadingWindowEditor")}</Typography>
      <Stack direction="row" spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Stack
          spacing={2}
          sx={{ flex: 1, minWidth: 0, minHeight: 0, overflow: "auto" }}
        >
          {windowsQuery.isError ||
          editsQuery.isError ||
          heatIssuesQuery.isError ||
          areasQuery.isError ? (
            <Alert severity="error">{t("failedToLoadData")}</Alert>
          ) : null}

          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => {
                goToDay(selectedDay.subtract(1, "day"));
              }}
            >
              <ArrowBackIosNewRoundedIcon />
            </IconButton>
            <Typography
              variant="body1"
              sx={{ minWidth: 100, textAlign: "center" }}
            >
              {selectedDay.format("YYYY-MM-DD")}
            </Typography>
            <IconButton
              onClick={() => {
                setDatePickerOpen(true);
              }}
            >
              <CalendarMonthRoundedIcon />
            </IconButton>
            <DatePicker
              open={datePickerOpen}
              onClose={() => {
                setDatePickerOpen(false);
              }}
              value={selectedDay}
              onChange={(date) => {
                if (date !== null)
                  goToDay(dayjs.utc(date.format("YYYY-MM-DD")));
                setDatePickerOpen(false);
              }}
              shouldDisableDate={(day) => day.isAfter(dayjs.utc(), "day")}
              slotProps={{ textField: { sx: { display: "none" } } }}
            />
            <IconButton
              onClick={() => {
                goToDay(selectedDay.add(1, "day"));
              }}
              disabled={selectedDay.add(1, "day").isAfter(dayjs.utc(), "day")}
            >
              <ArrowForwardIosRoundedIcon />
            </IconButton>

            <Box sx={{ flexGrow: 1 }} />

            <Button
              startIcon={<AddRoundedIcon />}
              onClick={openNewWindow}
              disabled={buckets.length === 0}
            >
              {t("newWindow")}
            </Button>

            <Button
              startIcon={<EditRoundedIcon />}
              onClick={() => {
                openWindow(selectedKey);
              }}
              disabled={selectedWindow === null}
            >
              {t("editWindow")}
            </Button>
            <Button
              startIcon={<DeleteOutlineRoundedIcon />}
              color="error"
              onClick={() => {
                if (selectedKey !== null) deleteWindow(selectedKey);
              }}
              disabled={selectedWindow === null}
            >
              {t("deleteWindow")}
            </Button>
            <Button
              onClick={() => {
                setDesired(originalKeyed);
              }}
              disabled={claims.length === 0}
            >
              {t("discard")}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setCommitOpen(true);
              }}
              disabled={claims.length === 0}
            >
              {t("applyEdits")} ({claims.length})
            </Button>
          </Stack>

          <Stack direction="row" alignItems="center">
            <Box sx={{ flex: 1 }}>
              <Button
                size="small"
                startIcon={<ShowChartRoundedIcon />}
                variant={traceOpen ? "outlined" : "text"}
                onClick={toggleTrace}
              >
                {t("scaleWeight")}
              </Button>
            </Box>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ flex: 1, justifyContent: "flex-end" }}
            >
              <Tooltip title={t("panLeft")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    panBy(-0.25);
                  }}
                >
                  <ChevronLeftRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("zoomOut")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    zoomBy(1.5);
                  }}
                >
                  <ZoomOutRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("fitToDay")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    setDomain(null);
                  }}
                >
                  <RestartAltRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("zoomIn")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    zoomBy(1 / 1.5);
                  }}
                >
                  <ZoomInRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("panRight")}>
                <IconButton
                  size="small"
                  onClick={() => {
                    panBy(0.25);
                  }}
                >
                  <ChevronRightRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {!windowsQuery.isLoading && buckets.length === 0 ? (
            <Typography color="text.secondary">
              {t("noLoadingWindows")}
            </Typography>
          ) : (
            <Box sx={{ position: "relative", flexShrink: 0 }}>
              <LoadingWindowTimeline
                buckets={timelineBuckets}
                windows={[...timelineWindows, ...proposalWindows]}
                drops={allTimelineDrops}
                chargeEvents={timelineChargeEvents}
                nonEditableBuckets={nonEditableBuckets}
                highlightedToAreas={new Set(highlightedToAreas)}
                cursorMs={videoOpen ? videoCursorMs : null}
                laneDotsVisible={traceOpen}
                domainStartMs={effectiveDomain.startMs}
                domainEndMs={effectiveDomain.endMs}
                selectedId={selectedKey}
                onSelectWindow={setSelectedKey}
                onEditWindow={openWindow}
                onResizeWindow={resizeWindow}
                onCreateWindow={createWindow}
                onDomainChange={(startMs, endMs) => {
                  setDomain(clampDomain({ startMs, endMs }));
                }}
              />
              {traceOpen ? (
                <MassTraceOverlay
                  tenantName={tenantName}
                  buckets={timelineBuckets}
                  domainStartMs={effectiveDomain.startMs}
                  domainEndMs={effectiveDomain.endMs}
                  periodStart={periodStart}
                  periodEnd={periodEnd}
                  initialAreas={traceInitialAreas}
                  selectedAreas={traceSelectedAreas}
                  onSelectedAreasChange={setTraceSelectedAreas}
                  bgAlpha={traceBgAlpha}
                  onBgAlphaChange={setTraceBgAlpha}
                  cursorMs={videoOpen ? videoCursorMs : null}
                  onClose={() => {
                    setTraceOpen(false);
                  }}
                />
              ) : null}
            </Box>
          )}

          {allTimelineDrops.length > 0 ? (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              sx={{ flexWrap: "wrap", flexShrink: 0 }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                {DROP_LEGEND.map((entry) => (
                  <Stack
                    key={entry.key}
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        bgcolor: entry.color,
                        borderRadius: 0.5,
                      }}
                    />
                    <Typography variant="body2">{t(entry.key)}</Typography>
                  </Stack>
                ))}
              </Stack>
              {availableToAreas.length > 0 ? (
                <Autocomplete
                  multiple
                  disableCloseOnSelect
                  size="small"
                  options={availableToAreas}
                  value={highlightedToAreas}
                  onChange={(_event, value) => {
                    setHighlightedToAreas(value);
                  }}
                  sx={{ minWidth: 240, maxWidth: "100%", width: "fit-content" }}
                  renderInput={(params) => (
                    <TextField {...params} label={t("highlightDropsByArea")} />
                  )}
                />
              ) : null}
            </Stack>
          ) : null}

          <Box sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              onClick={() => {
                setVideoOpen((open) => !open);
              }}
              startIcon={
                videoOpen ? (
                  <ExpandLessRoundedIcon />
                ) : (
                  <ExpandMoreRoundedIcon />
                )
              }
            >
              {t("videoPlayer")}
            </Button>
            <Collapse in={videoOpen} mountOnEnter>
              <VideoPanel
                windowStart={videoWindowStart}
                windowEnd={videoWindowEnd}
                onRecenter={recenterVideo}
                onVideoTimeChange={handleVideoTime}
                storageKey="loadingWindowEditor"
              />
            </Collapse>
          </Box>

          <Box sx={{ flex: 1, minHeight: 400 }}>
            <TitledTabs
              selectedTabKey={selectedTabKey}
              onChange={setSelectedTabKey}
              tabSpecs={ledgerTabSpecs}
            />
          </Box>

          <LoadingWindowEditDialog
            value={dialogWindow}
            buckets={buckets}
            heatOptions={heatOptions}
            onSave={saveWindow}
            onDelete={deleteWindow}
            onClose={() => {
              setDialogWindow(null);
            }}
          />

          <Dialog
            open={commitOpen}
            onClose={() => {
              setCommitOpen(false);
            }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>{t("applyEdits")}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("applyEditsSummary", {
                  loading: loadingCount,
                  idle: idleCount,
                })}
              </Typography>
              <Box sx={{ maxHeight: 260, overflow: "auto" }}>
                {claims.map((claim) => (
                  <Typography
                    key={`${claim.bucket}:${claim.startMs}:${claim.targetStatus}`}
                    variant="body2Mono"
                    sx={{ display: "block" }}
                  >
                    {`${claim.bucket} · ${dayjs
                      .utc(claim.startMs)
                      .format(SPAN_FORMAT)}–${dayjs
                      .utc(claim.endMs)
                      .format(SPAN_FORMAT)} · ${
                      claim.targetStatus === "loading"
                        ? `${claim.heatId ?? ""}·${claim.basketNumber ?? ""}`
                        : t("idle")
                    }`}
                  </Typography>
                ))}
              </Box>
              {submitState.isError ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {t("failedToApplyEdits")}
                </Alert>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setCommitOpen(false);
                }}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  void commit();
                }}
                disabled={submitState.isLoading}
              >
                {t("apply")}
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>

        <LoadingReviewPane
          proposals={pendingProposals}
          marks={reviewMarks}
          amendedIds={new Set(amendments.keys())}
          selectedId={selectedProposal?.id ?? null}
          decidedCount={reviewMarks.size + amendments.size}
          submitting={reviewState.isLoading}
          onMark={markReview}
          onMarkAll={markUnreviewed}
          onSelect={focusProposal}
          onSubmit={() => {
            void submitReview();
          }}
        />
      </Stack>
    </Stack>
  );
};
